import React, { useCallback, useRef } from 'react';
import { Button, Space } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import {
  sanitizePrintFilename,
  usePrintDocumentTitle,
  runPrintWithDocumentTitle,
} from '@/utils/printDocumentTitle';
import {
  installSalesQuotationPrintPageMargins,
  isSalesQuotationPrintRoot,
} from '@/utils/salesQuotationPrintPage';
import './print-preview-shell.css';

export type PrintPreviewChromeProps = {
  title: string;
  /** Document number used as Save-as-PDF filename (e.g. DN-2026-0015). */
  fileName?: string;
  onBack: () => void;
  children: React.ReactNode;
  /** Optional actions shown beside Print (screen only). */
  extraToolbar?: React.ReactNode;
};

type PrintProfile = 'letter' | 'a4-landscape' | 'a4-portrait';

const PRINT_CONTAINER_OVERRIDE_ID = 'print-container-qtn-overrides';

const PRINT_CONTAINER_QTN_CSS = `
@media print {
  /* Default edge-to-edge. Sales quotation clearance uses repeating thead/tfoot bands. */
  @page { size: letter; margin: 0; }
  html, body {
    height: auto !important;
    width: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    color: #000 !important;
  }
  #print-container {
    display: block !important;
    height: auto !important;
    margin: 0 auto !important;
    overflow: visible !important;
    padding: 0 !important;
    width: 8.5in !important;
  }
  #print-container .print-doc-a4 {
    border: none !important;
    box-shadow: none !important;
    margin: 0 !important;
    max-width: none !important;
    min-height: 0 !important;
    padding: 0 !important;
  }
  #print-container .printable-delivery-note,
  #print-container .printable-packing-list {
    display: flex !important;
    flex-direction: column !important;
  }
  #print-container .printable-quotation {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
    width: 8.5in !important;
    max-width: 8.5in !important;
    page-break-after: auto !important;
  }
  /* Shared print-engine composed pages */
  #print-container .print-paginated-root,
  #print-container .qtn-paginated-root {
    height: auto !important;
    min-height: 0 !important;
    page-break-after: auto !important;
  }
  #print-container .print-composed-page,
  #print-container .qtn-composed-page {
    page-break-after: always !important;
    page-break-inside: avoid !important;
  }
  #print-container .print-composed-page:last-child,
  #print-container .qtn-composed-page:last-child {
    page-break-after: auto !important;
  }
  #print-container .printable-quotation,
  #print-container .printable-quotation * {
    color: #000 !important;
    -webkit-text-fill-color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    visibility: visible !important;
    opacity: 1 !important;
  }
  /* Legacy spacer bands (quotation pre-shell) — keep harmless if absent */
  #print-container .qtn-print-band--header {
    display: block !important;
    height: 1.25in !important;
  }
  #print-container .qtn-print-band--footer {
    display: block !important;
    height: 1.15in !important;
  }
  /* Delivery / packing / PO / SAR: keep in-flow letterhead (single-page flush). */
  #print-container .printable-delivery-note .qtn-letterhead-header,
  #print-container .printable-delivery-note .qtn-letterhead-footer,
  #print-container .printable-packing-list .qtn-letterhead-header,
  #print-container .printable-packing-list .qtn-letterhead-footer,
  #print-container .printable-purchase-order .qtn-letterhead-header,
  #print-container .printable-purchase-order .qtn-letterhead-footer,
  #print-container .printable-sar .qtn-letterhead-header,
  #print-container .printable-sar .qtn-letterhead-footer {
    position: relative !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    bottom: auto !important;
  }
  #print-container .printable-delivery-note .qtn-letterhead-footer,
  #print-container .printable-packing-list .qtn-letterhead-footer,
  #print-container .printable-purchase-order .qtn-letterhead-footer,
  #print-container .printable-sar .qtn-letterhead-footer {
    margin-top: auto !important;
  }
  /* Delivery / packing letterhead: in-flow footer flush to Letter bottom (no gap under image). */
  #print-container .printable-delivery-note--letterhead,
  #print-container .printable-packing-list--letterhead {
    display: flex !important;
    flex-direction: column !important;
    height: 11in !important;
    min-height: 11in !important;
    max-height: 11in !important;
    overflow: hidden !important;
    padding: 0 !important;
    position: relative !important;
    width: 8.5in !important;
  }
  #print-container .printable-delivery-note--letterhead .qtn-letterhead-footer,
  #print-container .printable-packing-list--letterhead .qtn-letterhead-footer,
  #print-container .printable-delivery-note--letterhead .dn-flush-footer,
  #print-container .printable-packing-list--letterhead .dn-flush-footer {
    bottom: auto !important;
    left: auto !important;
    margin: auto 0 0 !important;
    position: relative !important;
    right: auto !important;
    top: auto !important;
    width: 100% !important;
  }
  #print-container .printable-delivery-note--letterhead .qtn-page-content,
  #print-container .printable-packing-list--letterhead .qtn-page-content {
    flex: 1 1 auto !important;
    padding-bottom: 4mm !important;
  }
  /* Preprinted stationery — keep physical letterhead bands */
  #print-container .printable-delivery-note--preprinted,
  #print-container .printable-packing-list--preprinted {
    display: flex !important;
    flex-direction: column !important;
    height: 11in !important;
    max-height: 11in !important;
    min-height: 11in !important;
    overflow: hidden !important;
    padding: 1.15in 0.35in 0.85in !important;
    width: 8.5in !important;
  }
  #print-container .printable-delivery-note--preprinted .qtn-page-content,
  #print-container .printable-packing-list--preprinted .qtn-page-content {
    flex: 1 1 auto !important;
    padding: 0 !important;
  }
  #print-container .print-doc-letter-dn {
    height: 11in !important;
    margin: 0 !important;
    overflow: hidden !important;
    padding: 0 !important;
    width: 8.5in !important;
  }
}
`;

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function waitForLetterheadImages(root: ParentNode): Promise<void> {
  const imgs = [...root.querySelectorAll('img.qtn-letterhead')] as HTMLImageElement[];
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) resolve();
          else {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }
        })
    )
  ).then(() => undefined);
}

/** QTN docs (delivery, packing, etc.) — print-container flow matches quotation modal print. */
function printQtnViaPrintContainer(innerHtml: string, fileName?: string): void {
  const mainContent = document.getElementById('root');
  const prevRootDisplay = mainContent?.style.display ?? '';

  let overrideEl = document.getElementById(PRINT_CONTAINER_OVERRIDE_ID) as HTMLStyleElement | null;
  if (!overrideEl) {
    overrideEl = document.createElement('style');
    overrideEl.id = PRINT_CONTAINER_OVERRIDE_ID;
    overrideEl.textContent = PRINT_CONTAINER_QTN_CSS;
    document.head.appendChild(overrideEl);
  }

  const existing = document.getElementById('print-container');
  if (existing) existing.remove();

  const printContainer = document.createElement('div');
  printContainer.id = 'print-container';
  printContainer.innerHTML = innerHtml;
  document.body.appendChild(printContainer);

  if (mainContent) mainContent.style.display = 'none';

  const uninstallSalesQuotationMargins = isSalesQuotationPrintRoot(printContainer)
    ? installSalesQuotationPrintPageMargins()
    : () => undefined;

  let printed = false;
  const cleanup = (): void => {
    uninstallSalesQuotationMargins();
    printContainer.remove();
    if (mainContent) mainContent.style.display = prevRootDisplay;
    window.removeEventListener('afterprint', cleanup);
  };

  const runPrintOnce = (): void => {
    if (printed) return;
    printed = true;
    runPrintWithDocumentTitle(fileName, () => window.print());
    setTimeout(cleanup, 1500);
  };

  window.addEventListener('afterprint', cleanup);

  void waitForLetterheadImages(printContainer).then(() => {
    requestAnimationFrame(() => requestAnimationFrame(runPrintOnce));
  });
  setTimeout(runPrintOnce, 3500);
}

/**
 * Chrome/Edge often show a blank print preview for SPA roots (layout + @page + compositing).
 * QTN letterhead docs use #print-container; other docs use a sized off-screen iframe.
 */
export function printContainerInIframe(container: HTMLElement, fileName?: string): void {
  if (container.querySelector('.printable-quotation')) {
    printQtnViaPrintContainer(container.innerHTML, fileName);
    return;
  }

  const printProfile: PrintProfile = container.querySelector(
    '.print-doc-a4--dimensional-landscape, .rs-operator-root, .print-composed-page--a4-landscape'
  )
    ? 'a4-landscape'
    : container.querySelector('.print-doc-a4, .print-composed-page--a4')
      ? 'a4-portrait'
      : 'letter';
  const iframeSizeStyle =
    printProfile === 'a4-landscape'
      ? 'position:fixed;left:-9999px;top:0;width:297mm;height:210mm;border:0;visibility:visible;pointer-events:none'
      : printProfile === 'a4-portrait'
        ? 'position:fixed;left:-9999px;top:0;width:210mm;height:297mm;border:0;visibility:visible;pointer-events:none'
        : 'position:fixed;left:-9999px;top:0;width:8.5in;height:11in;border:0;visibility:visible;pointer-events:none';
  const pageSizeRule =
    printProfile === 'a4-landscape'
      ? 'A4 landscape'
      : printProfile === 'a4-portrait'
        ? 'A4 portrait'
        : 'letter';
  const printContainerWidthRule =
    printProfile === 'a4-landscape' ? '297mm' : printProfile === 'a4-portrait' ? '210mm' : '8.5in';

  const iframe = document.createElement('iframe');
  iframe.setAttribute('style', iframeSizeStyle);
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    runPrintWithDocumentTitle(fileName, () => window.print());
    return;
  }

  const theme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  const safeTitle = sanitizePrintFilename(fileName ?? '');

  const headParts: string[] = ['<meta charset="utf-8"/>'];
  if (safeTitle) {
    headParts.push(`<title>${escapeHtmlAttr(safeTitle)}</title>`);
  }
  document.querySelectorAll('link[rel="stylesheet"]').forEach((el) => {
    const link = el as HTMLLinkElement;
    const href = link.getAttribute('href');
    if (href) {
      const abs = new URL(href, document.baseURI).href;
      headParts.push(`<link rel="stylesheet" href="${abs.replace(/"/g, '&quot;')}"/>`);
    }
  });
  document.querySelectorAll('style').forEach((el) => {
    headParts.push(`<style>${el.innerHTML}</style>`);
  });

  headParts.push(`<style>
    @page { size: ${pageSizeRule}; margin: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    body.print-iframe-body {
      width: ${printContainerWidthRule} !important;
      min-width: ${printContainerWidthRule} !important;
      overflow: visible !important;
    }
    body.print-iframe-body .print-doc-a4 {
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      min-height: 0 !important;
      padding: 0 !important;
    }
    /* Operator route sheet: printable side margins (iframe @page margin is 0) */
    body.print-iframe-body .rs-operator-page {
      padding: 10mm 14mm !important;
      box-sizing: border-box !important;
    }
    .print-preview-toolbar { display: none !important; }
    @media print {
      html, body { height: auto !important; width: auto !important; }
      body.print-iframe-body {
        width: ${printContainerWidthRule} !important;
        min-width: ${printContainerWidthRule} !important;
      }
      body.print-iframe-body .rs-operator-page {
        padding: 10mm 14mm !important;
      }
      .printable-quotation,
      .printable-quotation * {
        color: #000 !important;
        -webkit-text-fill-color: #000 !important;
        visibility: visible !important;
        opacity: 1 !important;
      }
    }
  </style>`);

  doc.open();
  doc.write(
    `<!DOCTYPE html><html data-theme="${theme}" lang="en"><head>${headParts.join(
      ''
    )}</head><body class="ant-app print-iframe-body">${container.innerHTML}</body></html>`
  );
  doc.close();

  const cleanup = (): void => {
    try {
      iframe.remove();
    } catch {
      /* ignore */
    }
  };

  let printed = false;
  const runPrintOnce = (): void => {
    if (printed) return;
    printed = true;
    runPrintWithDocumentTitle(fileName, () => {
      win.focus();
      win.print();
    });
    setTimeout(cleanup, 1500);
  };

  const schedulePrint = (): void => {
    void doc.fonts.ready.then(() =>
      waitForLetterheadImages(doc.body).then(() => {
        requestAnimationFrame(() => requestAnimationFrame(runPrintOnce));
      })
    );
  };

  const links = [...doc.querySelectorAll('link[rel="stylesheet"]')] as HTMLLinkElement[];
  if (links.length === 0) {
    schedulePrint();
    setTimeout(runPrintOnce, 2500);
    return;
  }

  let remaining = links.length;
  const onSheetDone = (): void => {
    remaining -= 1;
    if (remaining <= 0) schedulePrint();
  };
  links.forEach((l) => {
    l.addEventListener('load', onSheetDone);
    l.addEventListener('error', onSheetDone);
  });
  setTimeout(() => {
    if (remaining > 0) runPrintOnce();
  }, 3500);
}

/**
 * Screen-first print flow: Back + Print toolbar (hidden in @media print via .no-print).
 * Print uses #print-container for QTN layouts; iframe fallback for other documents.
 */
export function PrintPreviewChrome({
  title,
  fileName,
  onBack,
  children,
  extraToolbar,
}: PrintPreviewChromeProps) {
  const documentRef = useRef<HTMLDivElement>(null);
  usePrintDocumentTitle(fileName);

  const handlePrint = useCallback(() => {
    const el = documentRef.current;
    if (el && el.innerHTML.trim().length > 0) {
      printContainerInIframe(el, fileName);
    } else {
      runPrintWithDocumentTitle(fileName, () => window.print());
    }
  }, [fileName]);

  return (
    <div className="print-preview-page">
      <div className="print-preview-toolbar no-print">
        <Button type="default" icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <span className="print-preview-toolbar-title">{title}</span>
        <Space>
          {extraToolbar}
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      </div>
      <div ref={documentRef} className="print-preview-document">
        {children}
      </div>
    </div>
  );
}
